import { FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useIntroducingBrokerById } from '@pages/IntroducingBroker/hooks/useIntroducingBrokerById'
import { useIntroducingBrokerLegalDocuments } from '@pages/IntroducingBroker/hooks/useIntroducingBrokerLegalDocuments'

import { usePageNotice } from '@ui/v2/PageNotice/PageNoticeContext'

import { useWindowResize } from '@utils/domUtils'

import { useIBLink } from '@hooks/useIBLink'
import { useIBReferralCodes } from '@hooks/useIBReferralCodes'

import { useLoyaltyData } from '../IntroducingBroker'
import { LoyaltyProgram } from './LoyaltyProgram/LoyaltyProgram'
import { MultiLevel } from './MultiLevel/MultiLevel'
import { QuickPerformance } from './QuickPerformance/QuickPerformance'
import { ReferralLink } from './ReferralLink/ReferralLink'
import { TransactionHistory } from './TransactionHistory/TransactionHistory'
import { Wallets } from './Wallets/Wallets'
import { useConsumeLoyaltyReward } from './hooks/useConsumeLoyaltyReward'
import { useIBPerformance } from './hooks/useIBPerformance'
import { useMultiLevelCommission } from './hooks/useMultiLevelCommission'
import { useWalletsWithLatestTransactions } from './hooks/useWalletsWithLatestTransactions'

import styles from './IBDashboardPage.module.scss'

export const IBDashboardPage: FC = () => {
  const isMobile = useWindowResize()
  const { ibLoyaltyData, isLoyaltyDataLoading, refreshIbLoyaltyData } = useLoyaltyData()
  const { data: introducingBroker, isLoading: isIBLoading } = useIntroducingBrokerById()
  const { t } = useTranslation()
  const { addNotice } = usePageNotice()

  const addDisclaimerNotice = useCallback(() => {
    addNotice.attention({
      id: 'dashboard-disclaimer',
      canClose: true,
      title: t('IB.Dashboard.Disclaimer.title'),
      message: t('IB.Dashboard.Disclaimer.text'),
      paths: ['/dashboard/introducing-broker/dashboard'],
      dismissType: 'temporary',
      dismissDuration: 14,
    })
  }, [addNotice, t])

  useEffect(() => {
    addDisclaimerNotice()
  }, [addDisclaimerNotice])

  const {
    walletsWithLatestTransactions,
    isLoading: isWalletsLoading,
    refreshWalletsWithLatestTransactions,
  } = useWalletsWithLatestTransactions()

  const {
    data: ibData,
    isLoading: isIbDataLoading,
    forceRefresh: forceIBRefresh,
  } = useIntroducingBrokerById()

  const {
    ibCode,
    mtCode,
    isLoading: isLoadingReferralCodes,
    legalDocumentsSchemes,
  } = useIBReferralCodes()

  const { data: legalDocuments, isLoading: isLegalDocumentsLoading } =
    useIntroducingBrokerLegalDocuments({
      legalDocumentsSchemes,
    })

  const { ibLink } = useIBLink(ibCode)

  const {
    data: ibPerformanceData,
    isLoading: ibPerformanceDataLoading,
    dateRange,
    setDateRange,
  } = useIBPerformance()

  const handleReloadData = () => {
    refreshIbLoyaltyData?.()
    forceIBRefresh?.()
    refreshWalletsWithLatestTransactions?.()
  }

  const { consumeLoyaltyReward, isLoading: isConsumeLoyaltyRewardLoading } =
    useConsumeLoyaltyReward({
      onComplete: handleReloadData,
    })

  const {
    data: multiLevelData,
    isLoading: isMultiLevelDataLoading,
    dismissNotification,
  } = useMultiLevelCommission({
    isPaused: !ibData?.hasMultiLevelCommission,
  })

  const isMultiLevelLoading = isLegalDocumentsLoading || isIbDataLoading || isMultiLevelDataLoading
  const isLoyaltyProgramLoading = isLoyaltyDataLoading || isIbDataLoading

  return (
    <div className={styles.ibDashboardPage}>
      <div className={styles.col}>
        <Wallets
          walletsWithLatestTransactions={walletsWithLatestTransactions}
          isLoading={isWalletsLoading}
        />
        <TransactionHistory
          walletsWithLatestTransactions={walletsWithLatestTransactions}
          isLoading={isWalletsLoading}
        />
        {!isMobile && (
          <QuickPerformance
            isLoading={ibPerformanceDataLoading}
            ibPerformanceData={ibPerformanceData}
            className={styles.section}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        )}
      </div>
      <div className={styles.col}>
        {(isMultiLevelLoading || ibData?.hasMultiLevelCommission) && (
          <MultiLevel
            isLoading={isMultiLevelLoading}
            legalDocuments={legalDocuments ?? []}
            multiLevelData={multiLevelData ?? undefined}
            dismissNotification={dismissNotification}
          />
        )}
        {(isIBLoading || isLoyaltyProgramLoading || introducingBroker?.loyaltyProgramEnabled) && (
          <LoyaltyProgram
            isLoading={isLoyaltyProgramLoading || isIBLoading}
            loyaltyData={ibLoyaltyData ?? undefined}
            onClaimClick={consumeLoyaltyReward}
            ibData={ibData}
            isConsumeLoyaltyRewardLoading={isConsumeLoyaltyRewardLoading}
          />
        )}
        <ReferralLink
          isLoading={isLoadingReferralCodes}
          ibCode={ibCode}
          ibLink={ibLink}
          mtCode={mtCode}
        />
        {isMobile && (
          <QuickPerformance
            isLoading={ibPerformanceDataLoading}
            ibPerformanceData={ibPerformanceData}
            className={styles.section}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        )}
      </div>
    </div>
  )
}
